import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

const MAX_ZOOM = 4;
const OUTPUT_SIZE = 512;

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));
const pointerDistance = (a, b) => Math.hypot(a.x - b.x, a.y - b.y);

export default function ProfilePhotoEditor({ file, renk, t, saving = false, onCancel, onSave }) {
  const cropRef = useRef(null);
  const imageRef = useRef(null);
  const pointersRef = useRef(new Map());
  const gestureRef = useRef(null);
  const [source, setSource] = useState("");
  const [natural, setNatural] = useState({ width: 0, height: 0 });
  const [cropSize, setCropSize] = useState(0);
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [error, setError] = useState("");
  const viewportSize = cropSize * 0.84;

  useEffect(() => {
    const url = URL.createObjectURL(file);
    setSource(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  useEffect(() => {
    const node = cropRef.current;
    if (!node) return undefined;
    const update = () => setCropSize(node.getBoundingClientRect().width || 0);
    update();
    const observer = typeof ResizeObserver !== "undefined" ? new ResizeObserver(update) : null;
    observer?.observe(node);
    window.addEventListener("resize", update);
    return () => {
      observer?.disconnect();
      window.removeEventListener("resize", update);
    };
  }, []);

  useEffect(() => {
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKeyDown = (event) => {
      if (event.key === "Escape" && !saving) onCancel();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => {
      document.body.style.overflow = previous;
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [onCancel, saving]);

  const geometry = useMemo(() => {
    if (!natural.width || !natural.height || !viewportSize) {
      return { baseScale: 1, maxX: 0, maxY: 0 };
    }
    const sideways = Math.abs(rotation % 180) === 90;
    const orientedWidth = sideways ? natural.height : natural.width;
    const orientedHeight = sideways ? natural.width : natural.height;
    const baseScale = Math.max(viewportSize / orientedWidth, viewportSize / orientedHeight);
    return {
      baseScale,
      maxX: Math.max(0, (orientedWidth * baseScale * zoom - viewportSize) / 2),
      maxY: Math.max(0, (orientedHeight * baseScale * zoom - viewportSize) / 2),
    };
  }, [natural.height, natural.width, rotation, viewportSize, zoom]);

  const clampOffset = useCallback((next) => ({
    x: clamp(next.x, -geometry.maxX, geometry.maxX),
    y: clamp(next.y, -geometry.maxY, geometry.maxY),
  }), [geometry.maxX, geometry.maxY]);

  useEffect(() => {
    setOffset((current) => clampOffset(current));
  }, [clampOffset]);

  const beginPointer = (event) => {
    if (saving) return;
    event.currentTarget.setPointerCapture?.(event.pointerId);
    pointersRef.current.set(event.pointerId, { x: event.clientX, y: event.clientY });
    const points = Array.from(pointersRef.current.values());
    if (points.length === 1) {
      gestureRef.current = { mode: "pan", start: points[0], offset };
    } else if (points.length === 2) {
      gestureRef.current = { mode: "pinch", distance: pointerDistance(points[0], points[1]), zoom };
    }
  };

  const movePointer = (event) => {
    if (!pointersRef.current.has(event.pointerId) || saving) return;
    pointersRef.current.set(event.pointerId, { x: event.clientX, y: event.clientY });
    const points = Array.from(pointersRef.current.values());
    const gesture = gestureRef.current;
    if (points.length === 1 && gesture?.mode === "pan") {
      setOffset(clampOffset({
        x: gesture.offset.x + points[0].x - gesture.start.x,
        y: gesture.offset.y + points[0].y - gesture.start.y,
      }));
    } else if (points.length === 2) {
      if (gesture?.mode !== "pinch") {
        gestureRef.current = { mode: "pinch", distance: pointerDistance(points[0], points[1]), zoom };
        return;
      }
      const nextZoom = clamp(gesture.zoom * (pointerDistance(points[0], points[1]) / Math.max(1, gesture.distance)), 1, MAX_ZOOM);
      setZoom(nextZoom);
    }
  };

  const endPointer = (event) => {
    pointersRef.current.delete(event.pointerId);
    const points = Array.from(pointersRef.current.values());
    gestureRef.current = points.length === 1 ? { mode: "pan", start: points[0], offset } : null;
  };

  const reset = () => {
    setZoom(1);
    setRotation(0);
    setOffset({ x: 0, y: 0 });
  };

  const rotate = () => {
    setRotation((value) => (value + 90) % 360);
    setZoom(1);
    setOffset({ x: 0, y: 0 });
  };

  const exportPhoto = async () => {
    if (!natural.width || !natural.height || !viewportSize || !source) return;
    setError("");
    try {
      // Ekranda zaten çözülmüş olan sınırlı boyutlu görseli yeniden kullan.
      // Özellikle iOS WKWebView'da ikinci bir decode bellek sıçraması yaratır.
      const image = imageRef.current;
      if (!image?.complete || !image.naturalWidth) throw new Error("IMAGE_NOT_READY");

      const canvas = document.createElement("canvas");
      canvas.width = OUTPUT_SIZE;
      canvas.height = OUTPUT_SIZE;
      const context = canvas.getContext("2d", { alpha: false });
      context.fillStyle = "#ffffff";
      context.fillRect(0, 0, OUTPUT_SIZE, OUTPUT_SIZE);
      const outputScale = OUTPUT_SIZE / viewportSize;
      context.translate(OUTPUT_SIZE / 2 + offset.x * outputScale, OUTPUT_SIZE / 2 + offset.y * outputScale);
      context.rotate((rotation * Math.PI) / 180);
      context.scale(geometry.baseScale * zoom * outputScale, geometry.baseScale * zoom * outputScale);
      context.drawImage(image, -natural.width / 2, -natural.height / 2, natural.width, natural.height);

      const blob = await new Promise((resolve, reject) => {
        canvas.toBlob((webp) => {
          if (webp) return resolve(webp);
          canvas.toBlob((jpeg) => jpeg ? resolve(jpeg) : reject(new Error("ENCODE_FAILED")), "image/jpeg", 0.9);
        }, "image/webp", 0.88);
      });
      await onSave(blob);
    } catch (exportError) {
      console.warn("[ProfilePhotoEditor] Fotoğraf hazırlanamadı:", exportError?.message);
      setError(t("profile.photo.editorError"));
    }
  };

  const imageTransform = `translate(-50%, -50%) translate(${offset.x}px, ${offset.y}px) rotate(${rotation}deg) scale(${geometry.baseScale * zoom})`;

  return (
    <div className="profilePhotoEditorOverlay" role="dialog" aria-modal="true" aria-label={t("profile.photo.editorTitle")} onClick={(event) => event.stopPropagation()}>
      <div className="profilePhotoEditorSheet">
        <div className="profilePhotoEditorHeader">
          <button type="button" onClick={onCancel} disabled={saving}>{t("common.cancel")}</button>
          <div><strong>{t("profile.photo.editorTitle")}</strong><span>{t("profile.photo.editorSubtitle")}</span></div>
          <button type="button" className="profilePhotoEditorSave" onClick={exportPhoto} disabled={saving || !natural.width}>
            {saving ? t("common.saving") : t("profile.photo.use")}
          </button>
        </div>

        <div
          ref={cropRef}
          className="profilePhotoCropStage"
          style={{ "--profile-color": renk || "#6366f1" }}
          onPointerDown={beginPointer}
          onPointerMove={movePointer}
          onPointerUp={endPointer}
          onPointerCancel={endPointer}
          onWheel={(event) => {
            event.preventDefault();
            setZoom((value) => clamp(value + (event.deltaY < 0 ? 0.12 : -0.12), 1, MAX_ZOOM));
          }}
        >
          {source && (
            <img
              ref={imageRef}
              src={source}
              alt=""
              draggable="false"
              onLoad={(event) => {
                setNatural({ width: event.currentTarget.naturalWidth, height: event.currentTarget.naturalHeight });
                setError("");
              }}
              onError={() => setError(t("profile.photo.unsupported"))}
              style={{ transform: imageTransform }}
            />
          )}
          <div className="profilePhotoCropShade" aria-hidden="true" />
          <div className="profilePhotoCropRing" aria-hidden="true" />
        </div>

        <p className="profilePhotoEditorHint">{t("profile.photo.editorHint")}</p>
        <div className="profilePhotoZoomRow">
          <span aria-hidden="true">−</span>
          <input
            type="range"
            min="1"
            max={MAX_ZOOM}
            step="0.01"
            value={zoom}
            onChange={(event) => setZoom(Number(event.target.value))}
            aria-label={t("profile.photo.zoom")}
            disabled={saving}
          />
          <span aria-hidden="true">＋</span>
        </div>
        <div className="profilePhotoEditorTools">
          <button type="button" onClick={rotate} disabled={saving}>↻ {t("profile.photo.rotate")}</button>
          <button type="button" onClick={reset} disabled={saving}>◎ {t("profile.photo.reset")}</button>
        </div>
        {error && <div className="profilePhotoEditorError">{error}</div>}
      </div>
    </div>
  );
}
